/**
 * Браузерные уведомления Inbox (Web Notifications API).
 *
 * Контракт inbox §C.7, архитектура §8 (REQ-INB-010, Q-INB-2 resolved).
 *
 * Privacy-default (ARCH-SEC-INB-002): OS-уведомление показывает **только `subject`**
 * по умолчанию; тело `content` — opt-in через localStorage preference (default `'off'`).
 * Обоснование: OS-уведомление уходит на lock-screen / Notification Center / общий экран
 * (поверхность, которой у каркаса не было, `transport=desktop` — backlog INB §11.5);
 * subject-only — parity почтовых клиентов на mobile.
 *
 * Defense-in-depth (ARCH-SEC-INB-001): `showNotificationIfHidden` повторно проверяет
 * `isSafeActionUrl` для `action_url` перед `window.location.href` в onclick.
 *
 * MVP-ограничение (TD-002): детекция «новых» идёт только на refetch'ах видимой
 * вкладки; длительно свёрнутая/закрытая вкладка не опрашивается → фоновое уведомление
 * не покажется. SW + Push API = backlog (активация `transport=desktop`).
 */

import {
  INBOX_PREF_BROWSER_NOTIFICATIONS,
  INBOX_PREF_NOTIFICATION_BODY,
} from './constants';
import { isSafeActionUrl, resolveMessageUrl } from './reference-routes';
import type { PSMessage } from '@/lib/api/types';

// ---------------------------------------------------------------------------
// Поддержка API (SSR-safe guards)
// ---------------------------------------------------------------------------

/** `Notification` API доступен в текущем окружении (browser, не SSR). */
export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Текущее состояние permission, или `null` если API не поддерживается. */
export function permissionState(): NotificationPermission | null {
  if (!notificationsSupported()) return null;
  return Notification.permission;
}

// ---------------------------------------------------------------------------
// Permission flow (AC-INB-010.3)
// ---------------------------------------------------------------------------

/**
 * Запрашивает `Notification.permission` **только по действию пользователя**
 * (click по `<InboxBell>`, или mount `<InboxView>`), если:
 * - `permission === 'default'` (не `granted`/`denied` — не дёргаем повторно, AC-INB-010.3);
 * - `hasUnread === true` (нет непрочитанных → не беспокоим, INB §11.2).
 *
 * UI показывает поясняющий текст **до** вызова этого метода (INB §11.2).
 * Возвращает финальное состояние permission (или `null` если API не поддерживается).
 */
export async function requestPermissionIfNeeded(
  hasUnread: boolean,
): Promise<NotificationPermission | null> {
  if (!notificationsSupported()) return null;
  if (Notification.permission !== 'default') return Notification.permission;
  if (!hasUnread) return 'default';
  // `requestPermission` требует user-gesture; вызывается из click-handler'a вызывающей стороны.
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

// ---------------------------------------------------------------------------
// Локальные преференсы (§C.7.3) — SSR-safe localStorage
// ---------------------------------------------------------------------------

/** Преференс «браузерные уведомления вкл/выкл» (default `'on'`). */
export function getNotificationPref(): 'on' | 'off' {
  if (typeof localStorage === 'undefined') return 'on';
  return localStorage.getItem(INBOX_PREF_BROWSER_NOTIFICATIONS) === 'off'
    ? 'off'
    : 'on';
}

export function setNotificationPref(v: 'on' | 'off'): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INBOX_PREF_BROWSER_NOTIFICATIONS, v);
}

/**
 * Преференс «показывать тело уведомления» (ARCH-SEC-INB-002, default `'off'`).
 * `off` → OS-уведомление только `subject` (privacy-default); `on` → subject + content.
 */
export function getNotificationBodyPref(): 'on' | 'off' {
  if (typeof localStorage === 'undefined') return 'off';
  return localStorage.getItem(INBOX_PREF_NOTIFICATION_BODY) === 'on'
    ? 'on'
    : 'off';
}

export function setNotificationBodyPref(v: 'on' | 'off'): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INBOX_PREF_NOTIFICATION_BODY, v);
}

// ---------------------------------------------------------------------------
// Показ уведомления (AC-INB-010.1/.2/.4) — privacy-default subject-only
// ---------------------------------------------------------------------------

/**
 * Показывает OS-уведомление для отдельного сообщения, **только если вкладка скрыта**
 * (`document.hidden`) И permission `'granted'` И преференс `'on'` (AC-INB-010.2).
 * При активной видимой вкладке — no-op (хватит badge/dropdown, концепция §14.3).
 *
 * Privacy-default (ARCH-SEC-INB-002): тело показывается только если
 * `getNotificationBodyPref() === 'on'`; default `'off'` → только `subject`.
 *
 * `n.onclick`: фокусировка вкладки + навигация по URL (resolveMessageUrl уже отфильтровал
 * через isSafeActionUrl; здесь повторная проверка defense-in-depth; fallback `/inbox`)
 * + mark-read после focus (AC-INB-010.4).
 *
 * @param onMarkRead callback для mark-read после клика (прокидывается из useInbox).
 */
export function showNotificationIfHidden(
  message: PSMessage,
  onMarkRead?: (message: PSMessage) => void,
): void {
  if (!shouldShow()) return;

  const showBody = getNotificationBodyPref() === 'on';
  const n = new Notification(message.subject, {
    body: showBody ? message.content ?? undefined : undefined,
    icon: '/logo/logo_192.png',
    tag: message.id, // дедупликация ОС (INB §11.1)
  });
  n.onclick = () => {
    window.focus();
    // resolveMessageUrl уже применил isSafeActionUrl; повторная проверка defense-in-depth
    // (защита от race: localStorage/legacy-данные).
    const url = resolveMessageUrl(message);
    const safe = url !== null && (url.startsWith('/') || isSafeActionUrl(url));
    if (safe) {
      window.location.href = url as string;
    } else {
      window.location.href = '/inbox';
    }
    if (onMarkRead) onMarkRead(message);
    n.close();
  };
}

/**
 * Показывает summary-уведомление «N новых уведомлений» (anti-spam, Q-INB-2: 3+ новых).
 * Тело summary privacy-safe — **не** содержит `subject`/`content` отдельных сообщений
 * (ARCH-SEC-INB-002 invariant; тело «Откройте Входящие» фиксированное).
 */
export function showSummaryNotification(newCount: number): void {
  if (!shouldShow()) return;
  const n = new Notification(
    `${newCount} ${pluralize(newCount, 'новое', 'новых', 'новых')} уведомление${pluralize(newCount, '', 'я', 'й')}`,
    {
      body: 'Откройте Входящие',
      icon: '/logo/logo_192.png',
      tag: 'inbox-summary',
    },
  );
  n.onclick = () => {
    window.focus();
    window.location.href = '/inbox';
    n.close();
  };
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function shouldShow(): boolean {
  return (
    notificationsSupported() &&
    Notification.permission === 'granted' &&
    typeof document !== 'undefined' &&
    document.hidden &&
    getNotificationPref() === 'on'
  );
}

/** Русская плюрализация для summary-заголовка (1/2-4/5+). */
function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
